/* oxlint-disable react/react-compiler -- Existing code is not yet compatible with React Compiler checks. */
import Fuse from 'fuse.js';
import { useMemo } from 'react';

import type { AnyMenuNode } from './tree';

const fuseParameters = {
    threshold: 0.3,
    location: 0,
    distance: 100,
    shouldSort: true,
    includeScore: true,
    minMatchCharLength: 1,
    isCaseSensitive: false,
    // Weight title higher than subtitle/keywords so direct title matches rank first
    keys: [
        { name: 'title', weight: 0.7 },
        { name: 'keywords', weight: 0.5 },
        { name: 'subtitle', weight: 0.3 },
    ],
};

function getNodeChildren(node: AnyMenuNode) {
    if (node.type === 'item' && 'children' in node && node.children) {
        return node.children.reduce<{ node: AnyMenuNode; title: string; keywords: string[] }[]>(
            (all, node) => {
                if ('parent' in node && node.parent) {
                    all.push({
                        node,
                        title: [node.parent.title, node.title].join(' > '),
                        keywords: [node.parent.title, node.title].filter((x): x is string =>
                            Boolean(x)
                        ),
                    });
                }
                return all;
            },
            []
        );
    }

    return [];
}

function getNodeFilterPriority(node: AnyMenuNode) {
    return node.type === 'item' ? node.filterPriority : null;
}

const nestedFuseParameters = {
    ...fuseParameters,
    keys: [
        ...fuseParameters.keys,
        { name: 'node.keywords', weight: 0.5 },
        { name: 'node.subtitle', weight: 0.3 },
    ],
};

/**
 * Filter menu nodes using Fuse.
 *
 * Both indexes are built on first use rather than up front: indexing walks every key of every
 * node, and a large content menu re-enters this hook with a fresh node array on each keystroke.
 * An unqueried menu — and a menu whose items expose no submenu children, which is the common
 * case — must not pay for an index it never searches.
 */
export function useFilterMenuNodes(nodes: AnyMenuNode[], query: string | null): AnyMenuNode[] {
    const getTopLevelFuse = useMemo(() => {
        let fuse: Fuse<AnyMenuNode> | null = null;
        return () => (fuse ??= new Fuse(nodes, fuseParameters));
    }, [nodes]);
    const getNestedFuse = useMemo(() => {
        let fuse: Fuse<ReturnType<typeof getNodeChildren>[number]> | null = null;
        return () => {
            if (!fuse) {
                fuse = new Fuse(nodes.flatMap(getNodeChildren), nestedFuseParameters);
            }
            return fuse;
        };
    }, [nodes]);
    return useMemo(() => {
        if (!query) {
            return nodes;
        }
        const pinnedNodes = nodes
            .filter((node) => getNodeFilterPriority(node) !== null)
            .sort((a, b) => {
                const priorityA = getNodeFilterPriority(a);
                const priorityB = getNodeFilterPriority(b);
                return (priorityA ?? 0) - (priorityB ?? 0);
            });
        const pinnedIds = new Set(pinnedNodes.map((node) => node.id));
        const matchedNodes = [
            ...getTopLevelFuse()
                .search(query)
                .map((r) => r.item),
            ...getNestedFuse()
                .search(query)
                .map((r) => r.item.node),
        ].filter((node) => !pinnedIds.has(node.id));
        return [...pinnedNodes, ...matchedNodes];
    }, [getTopLevelFuse, getNestedFuse, nodes, query]);
}
